import React, { Component } from 'react';
import { options } from 'preact'; // eslint-disable-line
import PropTypes from 'prop-types';
import classNames from 'classnames';
import querystring from 'querystring';
import { WhetherMobileHOC } from '../../hocs/whetherMobileHOC';
import PopularSearches from '../PopularSearches';
import SuggestionsList from '../SuggestionsList';
import ProductCategoriesList from '../ProductCategoriesList';
import ProductsList from '../ProductsList';
import SearchInput from '../SearchInput';
import MobileView from '../MobileView';
import Loader from '../Loader';
import ShowResultsBar from '../ShowResultsBar';
import IconSearch from '../IconSearch';
import OutsideClickHandler from '../OutsideClickHandler';
import SafePortal from '../SafePortal';

import request from '../../../utils/request';
import { debounce, getLocaleParams } from '../../../utils';
import {
    MIN_SEARCH_SYMBOLS_COUNT,
    DEFAULT_GLOBAL_SEARCH_TRANSLATIONS,
    POPULAR_SEARCHES_COUNT,
    APP_CONTAINER_ID_MOBILE,
} from '../../../consts';

import '../../../styles/scss/apc-header-from-sdl-side/styles/index.scss';
import styles from './index.scss';

import { focusMobileInput } from '../MobileSearchArea';

// Turn off async setState entirely to make focusing on input possible on iphone
options.debounceRendering = f => f();

const defaultSuggestions = {
    results: [],
    phrase: [],
    categories: [],
};

class App extends Component {
    state = {
        showMobile: false,
        isDropDownOpen: false,
        isLoading: false,
        isLoaded: false,
        searchInputValue: '',
        localeParams: {
            lc: '',
            cc: '',
        },
        requestUrl: {
            url: '',
            queryParamsObj: {
                Ntt: '',
                Nty: '',
                Dy: '',
                expandGlobalFacet: '',
            },
        },
        suggestions: {
            ...defaultSuggestions,
        },
        translationsMap: {
            // -- example of translations you can see in
            // -- 'DEFAULT_GLOBAL_SEARCH_TRANSLATIONS' constant --
        },
        noSuggestionsFound: false,
        showPopularSearches: false,
        isRequestCancelled: false,
    };

    lastRequest = null;

    static splitUrl = url => {
        const [urlWithoutQuery, urlQuery] = url.split('?');
        const queryParamsObj = querystring.urlQuery;

        return {
            url: urlWithoutQuery,
            queryParamsObj,
        };
    };

    static assembleUrl = ({ url, queryParamsObj }) =>
        `${url}?${querystring.encode(queryParamsObj)}`;

    componentDidMount() {
        const { lc, cc } = getLocaleParams();
        const showAllUrlString = `/shop/${cc}/${lc}/search`;
        const prevSearchedPhraseDOMNode = document.querySelector('.js-searched-phrase');
        const prevSearchedPhraseText = prevSearchedPhraseDOMNode
            ? prevSearchedPhraseDOMNode.value
            : '';

        this.setState(
            {
                requestUrl: {
                    ...this.state.requestUrl,
                    url: showAllUrlString,
                    queryParamsObj: {
                        Dy: '1',
                        Nty: '1',
                        expandGlobalFacet: '',
                        Ntt: prevSearchedPhraseText,
                    },
                },
                localeParams: {
                    lc,
                    cc,
                },
                searchInputValue: prevSearchedPhraseText,
            },
            () => {
                this.loadTranslations(lc, cc);
                if (prevSearchedPhraseText.length > 0) {
                    this.fetchProducts();
                }
            },
        );
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.isDropDownOpen !== this.state.isDropDownOpen) return;
        this.adjustSearchBarHeight();
        if (prevState.showMobile !== this.state.showMobile) {
            if (
                !this.state.showResultsButton &&
                this.state.showMobile &&
                !this.state.searchInputValue
            ) {
                this.addPopularSearches();
            } else {
                this.removePopularSearches();
            }
        }
    }

    loadTranslations = (lc, cc) => {
        request
            .get(process.env.API_GET_TRANSLATIONS, {
                params: {
                    translationsMapName: 'searchBox',
                    locale: `${lc.toLowerCase()}_${cc.toUpperCase()}`,
                },
            })
            .then(data => {
                const { translationsMap } = data;

                this.setState({
                    translationsMap,
                });
            })
            .catch(err => {
                console.error('Error loading languages: ', err);

                this.setState({
                    translationsMap: DEFAULT_GLOBAL_SEARCH_TRANSLATIONS,
                });
            });
    };

    /**
     * Adjust 'SearchBar' height
     * Show scroll on 'SearchBar' if it does not fit the screen
     */
    adjustSearchBarHeight = () => {
        if (!this.searchBarResults || !this.showResultsButton) return;

        const windowHeight = document.documentElement.clientHeight;
        const searchBarDistanceFromTop = this.searchBarResults.getBoundingClientRect().top;

        // searchBarDistanceFromBottom - distance from the bottom
        // (used '60' in accordance to our own opinion, due to the absence of the markup)
        const searchBarDistanceFromBottom = 60;
        const showAllResultsButtonHeight = this.showResultsButton.clientHeight;

        const isSearchBarOverlapWindow =
            this.searchBarResults.clientHeight + searchBarDistanceFromTop > windowHeight;

        if (!isSearchBarOverlapWindow) return;

        const updatedSearchBarHeight =
            windowHeight -
            searchBarDistanceFromTop -
            searchBarDistanceFromBottom -
            showAllResultsButtonHeight;

        this.searchBarResults.style.cssText = `
            overflow-x: hidden;
            overflow-y: scroll;
        `;
        this.searchBarResults.style.height = `${updatedSearchBarHeight}px`;
    };

    fetchProducts = () => {
        if (
            this.state.isRequestCancelled ||
            this.state.searchInputValue.length < MIN_SEARCH_SYMBOLS_COUNT
        ) {
            this.setState({
                isRequestCancelled: false,
            });
            return;
        }

        const {
            localeParams: { cc, lc },
        } = this.state;
        this.setState({ isLoading: true, suggestions: {} });

        const requestUrlFromConfig = process.env.API_GET_SUGGESTION;
        const requestUrl =
            requestUrlFromConfig &&
            requestUrlFromConfig.replace(/(:cc)\/(:lc)/gi, () => `${cc}/${lc}`);

        const suggestionRequest = request.get(requestUrl, {
            params: {
                ...this.state.requestUrl.queryParamsObj,
            },
        });

        this.lastRequest = suggestionRequest;

        // eslint-disable-next-line consistent-return
        return suggestionRequest.then(this.processResponse(suggestionRequest)).catch(err => {
            console.error(err);
            this.setState({ isLoading: false, isLoaded: false, isDropDownOpen: false });
        });
    };

    processResponse = currentRequest => ({ suggestions: { results, phrase, categories } }) => {
        if (currentRequest !== this.lastRequest) {
            return;
        }
        if (
            this.state.isRequestCancelled ||
            this.state.searchInputValue.length < MIN_SEARCH_SYMBOLS_COUNT
        ) {
            this.setState({
                isRequestCancelled: false,
                isLoading: false,
            });
            return;
        }

        const updater = {
            isLoading: false,
            isLoaded: !!results.length || !!phrase.length || !!categories.length,
            noSuggestionsFound: !results.length && !phrase.length && !categories.length,
            suggestions: { ...defaultSuggestions },
            requestUrl: {
                ...this.state.requestUrl,
            },
        };

        updater.suggestions = {
            results,
            phrase,
            categories,
        };

        this.setState(updater);
    };

    handleInputValueChange = newValue => {
        let suggestionsUpdater = {};

        if (newValue.length <= MIN_SEARCH_SYMBOLS_COUNT) {
            suggestionsUpdater = {
                suggestions: {
                    ...defaultSuggestions,
                },
                noSuggestionsFound: false,
                isLoaded: false,
            };
        }

        suggestionsUpdater.showPopularSearches = newValue.length === POPULAR_SEARCHES_COUNT;

        this.setState(
            {
                ...suggestionsUpdater,
                searchInputValue: newValue,
                isDropDownOpen:
                    newValue.length >= MIN_SEARCH_SYMBOLS_COUNT ||
                    newValue.length === POPULAR_SEARCHES_COUNT,
                requestUrl: {
                    ...this.state.requestUrl,
                    queryParamsObj: {
                        ...this.state.requestUrl.queryParamsObj,
                        Ntt: newValue,
                    },
                },
                isRequestCancelled: newValue.length < MIN_SEARCH_SYMBOLS_COUNT,
            },
            () => this.handleRequestSend(),
        );
    };

    handleClearButtonClick = () => {
        this.setState({
            searchInputValue: '',
            isRequestCancelled: true,
            isLoaded: false,
            isDropDownOpen: false,
            noSuggestionsFound: false,
            suggestions: { ...defaultSuggestions },
            requestUrl: {
                ...this.state.requestUrl,
                queryParamsObj: {
                    ...this.state.requestUrl.queryParamsObj,
                    Ntt: '',
                },
            },
        });
    };

    fetchProductsDebounced = debounce(this.fetchProducts, 400);

    handleRequestSend = () => {
        const { searchInputValue } = this.state;

        if (searchInputValue.length >= MIN_SEARCH_SYMBOLS_COUNT) {
            this.fetchProductsDebounced(searchInputValue);
        }
    };
    renderSearchResults = () => {
        const { suggestions, translationsMap } = this.state;
        const { results: products, categories, phrase } = suggestions;

        return (
            <div>
                {phrase && phrase.length ? (
                    <SuggestionsList phrase={phrase} searchValue={this.state.searchInputValue} />
                ) : null}
                {categories && categories.length ? (
                    <ProductCategoriesList
                        categories={categories}
                        translationsMap={translationsMap}
                    />
                ) : null}
                {products && products.length ? (
                    <ProductsList products={products} translationsMap={translationsMap} />
                ) : null}
            </div>
        );
    };

    /**
     * Redirect to Search Result Page
     */
    handleRedirect = () => {
        const { searchInputValue, requestUrl } = this.state;
        if (!searchInputValue.length) return;

        const showAllResultsURL = App.assembleUrl(requestUrl);

        if (!showAllResultsURL) return;

        window.location.assign(showAllResultsURL);
    };

    handleSearchButtonClick = () => {
        if (!this.state.searchInputValue.length) {
            this.setState({
                isDropDownOpen: false,
            });
        }
    };

    renderResultsPanel = () => {
        const {
            isLoading,
            noSuggestionsFound,
            translationsMap,
            searchInputValue,
            showPopularSearches,
            localeParams,
        } = this.state;

        return (
            <div className={styles.searchBar}>
                {isLoading ? (
                    <Loader text={translationsMap['searchBox.searching']} />
                ) : (
                    <div
                        className={styles.results}
                        ref={searchBar => {
                            this.searchBarResults = searchBar;
                            return null;
                        }}
                    >
                        {!noSuggestionsFound && this.renderSearchResults()}
                        {showPopularSearches && localeParams.cc === 'us' ? (
                            <PopularSearches />
                        ) : null}
                    </div>
                )}
                {searchInputValue.length >= MIN_SEARCH_SYMBOLS_COUNT ? (
                    <div
                        ref={showResultsButton => {
                            this.showResultsButton = showResultsButton;
                            return null;
                        }}
                    >
                        <ShowResultsBar
                            redirectTo={this.handleRedirect}
                            translationsMap={translationsMap}
                        />
                    </div>
                ) : null}
            </div>
        );
    };

    handleClick = () => {
        this.setState({ showMobile: true }, () => {
            focusMobileInput();
        });
    };

    handleToggle = () => {
        this.setState({ showMobile: !this.state.showMobile });
    };

    openDropDown = () => {
        this.setState(
            {
                isDropDownOpen: true,
            },
            () => !this.props.isMobile && this.adjustSearchBarHeight(),
        );
    };

    closeDropDown = () => {
        this.removePopularSearches();
        this.setState({
            isDropDownOpen: false,
            showPopularSearches: false,
        });
    };

    addPopularSearches = () => {
        this.setState({
            showPopularSearches: true,
        });
    };

    removePopularSearches = () => {
        this.setState({
            showPopularSearches: false,
        });
    };

    removeMobileContainer() {
        const el = document.getElementById(APP_CONTAINER_ID_MOBILE);
        if (el && el.parentNode) {
            el.parentNode.removeChild(el);
        }
    }

    render() {
        const { isMobile } = this.props;
        const { searchInputValue, isDropDownOpen, translationsMap } = this.state;

        const topContainerClass = classNames({
            [styles.appOuter]: true,
            globalSearch: true,
        });

        return (
            <div className={topContainerClass}>
                <div>
                    {isMobile ? (
                        <div>
                            <button
                                onClick={this.handleClick}
                                className={styles.mobileSearchInvokeButton}
                            >
                                <IconSearch width={30} height={30} />
                            </button>
                        </div>
                    ) : (
                        <div>
                            <OutsideClickHandler closeDropDown={this.closeDropDown}>
                                <SearchInput
                                    onChange={this.handleInputValueChange}
                                    onClear={this.handleClearButtonClick}
                                    redirectTo={this.handleRedirect}
                                    openDropDown={this.openDropDown}
                                    inputValue={searchInputValue}
                                    translationsMap={translationsMap}
                                    closeDropDown={this.closeDropDown}
                                    addPopularSearches={this.addPopularSearches}
                                    searchButtonClick={this.handleSearchButtonClick}
                                />
                                {isDropDownOpen ? this.renderResultsPanel() : null}
                            </OutsideClickHandler>
                        </div>
                    )}
                </div>
                <SafePortal into="body" unmountFallback={this.removeMobileContainer}>
                    <MobileView
                        isVisible={this.state.showMobile}
                        onChange={this.handleInputValueChange}
                        onClear={this.handleClearButtonClick}
                        onCancel={this.handleToggle}
                        mainState={{ ...this.state }}
                        redirectTo={this.handleRedirect}
                        addPopularSearches={this.addPopularSearches}
                    />
                </SafePortal>
                <SafePortal into="#mobileSearchField" unmountFallback={this.removeMobileContainer}>
                    <div className={styles.mobileSearchArea__outerWrap}>
                        <div
                            className={styles.mobileSearchArea__fieldWrap}
                            onClick={this.handleClick}
                        >
                            <input
                                className={styles.mobileSearchArea__field}
                                placeholder={translationsMap['search.fss.label.searchBox']}
                                value={this.state.searchInputValue}
                            />
                            <div className={styles.mobileSearchArea__submitWrap}>
                                <button className={styles.mobileSearchArea__submit} type="button">
                                    <IconSearch />
                                </button>
                            </div>
                        </div>
                    </div>
                </SafePortal>
            </div>
        );
    }
}

App.propTypes = {
    isMobile: PropTypes.bool.isRequired,
};

export default WhetherMobileHOC(App);
